import React, { useEffect, useState } from 'react';
import AuthenticatedFileButton from '../../components/AuthenticatedFileButton';
import prestacaoContaService, { type PrestacaoAnexo, type PrestacaoComprovante } from '../../services/prestacaoContaService';
import DownloadButtons from './DownloadButtons';
import PublicacaoAnexoControl from './PublicacaoAnexoControl';
import { useAuth } from '../../context/AuthContext';
import { Permission } from '../../types/user.types';
import { Table, Tbody, Td, Th, Thead, Tr } from '../../components/ui/Table';
import {
  accentTextClass, bodyTextClass, formatCurrency,
  fonteRecursoLabel, formatDate, labelTextClass, strongTextClass, subtleSurfaceClass, surfaceClass,
} from './shared';

type Props = {
  pcId: string;
};

const Anexo5PagamentosSection: React.FC<Props> = ({ pcId }) => {
  const [comprovantes, setComprovantes] = useState<PrestacaoComprovante[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { hasPermission } = useAuth();
  const podePublicar = hasPermission(Permission.PUBLICAR_EVIDENCIA_PRESTACAO);

  const atualizarAnexo = (atualizado: PrestacaoAnexo) => setComprovantes((prev) => prev.map((c) => ({
    ...c,
    anexos: c.anexos.map((a) => (a.id === atualizado.id ? { ...a, ...atualizado } : a)),
  })));

  const carregar = async () => {
    setLoading(true);
    setError(null);
    try {
      setComprovantes(await prestacaoContaService.getComprovantesPorParcela(pcId));
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao carregar os pagamentos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pcId]);

  const total = comprovantes.reduce((sum, c) => sum + Number(c.valor || 0), 0);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className={`text-lg font-semibold ${strongTextClass}`}>Anexo 5 · Pagamentos Realizados</h2>
        <DownloadButtons pcId={pcId} anexo="5" nomeArquivo="anexo-5-pagamentos-realizados" />
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
          {error}
        </div>
      )}

      <p className={`text-sm ${labelTextClass}`}>
        Relatório gerado a partir dos pagamentos da PC. Cadastre os pagamentos e seus comprovantes em Metas e Etapas Executadas.
      </p>

      {loading ? (
        <p className={`text-sm ${bodyTextClass}`}>Carregando...</p>
      ) : comprovantes.length === 0 ? (
        <p className={`text-sm ${labelTextClass}`}>Nenhum pagamento registrado nesta PC.</p>
      ) : (
        <>
          <div className={`overflow-x-auto rounded-2xl border ${surfaceClass}`}>
            <Table>
              <Thead className={subtleSurfaceClass}>
                <Tr>
                  <Th className={`px-4 py-2 text-left font-semibold ${labelTextClass}`}>Etapa</Th>
                  <Th className={`px-4 py-2 text-left font-semibold ${labelTextClass}`}>Credor</Th>
                  <Th className={`px-4 py-2 text-left font-semibold ${labelTextClass}`}>CPF/CNPJ</Th>
                  <Th className={`px-4 py-2 text-left font-semibold ${labelTextClass}`}>Documento</Th>
                  <Th className={`px-4 py-2 text-left font-semibold ${labelTextClass}`}>Transferência</Th>
                  <Th className={`px-4 py-2 text-right font-semibold ${labelTextClass}`}>Valor</Th>
                </Tr>
              </Thead>
              <Tbody>
                {comprovantes.map((c) => (
                  <Tr key={c.id} className="border-t border-slate-200 dark:border-slate-800">
                    <Td className={`px-4 py-2 ${bodyTextClass}`}>{c.itemTitulo || '—'}</Td>
                    <Td className={`px-4 py-2 ${strongTextClass}`}>{c.credorNome || '—'}</Td>
                    <Td className={`px-4 py-2 ${bodyTextClass}`}>{c.credorDocumento || '—'}</Td>
                    <Td className={`px-4 py-2 ${bodyTextClass}`}>
                      {c.numeroDocumentoFiscal || '—'}{c.dataDocumentoFiscal ? ` (${formatDate(c.dataDocumentoFiscal)})` : ''}
                    </Td>
                    <Td className={`px-4 py-2 ${bodyTextClass}`}>
                      {[c.numeroTransferencia, c.formaTransferencia, c.bancoTransferencia].filter(Boolean).join(' · ') || '—'}
                      <div className="text-xs">Pagamento: {formatDate(c.dataPagamento)} · {fonteRecursoLabel(c.fonteRecurso)}</div>
                    </Td>
                    <Td className={`px-4 py-2 text-right ${strongTextClass}`}>{formatCurrency(c.valor)}</Td>
                  </Tr>
                ))}
                <Tr className={`border-t border-slate-300 font-semibold dark:border-slate-700 ${subtleSurfaceClass}`}>
                  <Td className={`px-4 py-2 ${strongTextClass}`} colSpan={5}>Total</Td>
                  <Td className={`px-4 py-2 text-right ${strongTextClass}`}>{formatCurrency(total)}</Td>
                </Tr>
              </Tbody>
            </Table>
          </div>

          <div className="space-y-2">
            {comprovantes.map((c) => c.anexos.length > 0 && (
              <div key={c.id} className="flex flex-wrap gap-2">
                {c.anexos.map((anexo) => (
                  <div key={anexo.id} className="flex flex-wrap items-center gap-2">
                    <AuthenticatedFileButton
                      url={anexo.viewUrl || ''}
                      fileName={anexo.nomeOriginal}
                      disabled={!anexo.viewUrl}
                      className={`flex items-center gap-2 rounded-xl px-3 py-1.5 text-left text-xs ${subtleSurfaceClass} ${accentTextClass}`}
                    >
                      <span>{anexo.nomeOriginal}</span>
                    </AuthenticatedFileButton>
                    {podePublicar
                      ? <PublicacaoAnexoControl anexo={anexo} onChange={atualizarAnexo} />
                      : anexo.publicavel && <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">No portal</span>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Anexo5PagamentosSection;
